import React, { useEffect } from 'react';
import { X, Menu } from 'lucide-react';
import { trapFocus } from '../../utils/accessibility';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  links: Array<{
    href: string;
    label: string;
  }>;
}
/**
 * Mobile navigation menu with accessibility support
 * Includes focus trapping and proper ARIA attributes
 */
export function MobileMenu({
  isOpen,
  onClose,
  links
}: MobileMenuProps) {
  useEffect(() => {
    if (!isOpen) return;
    // Trap focus within menu
    const menuElement = document.getElementById('mobile-menu');
    if (!menuElement) return;
    const cleanup = trapFocus(menuElement);
    // Prevent body scroll when menu is open
    document.body.style.overflow = 'hidden';
    return () => {
      cleanup();
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden" onClick={onClose} aria-hidden="true" data-id="element-2023" />

      {/* Menu Panel */}
      <div id="mobile-menu" className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-[#0a0a0a] border-l border-white/10 z-50 md:hidden overflow-y-auto" role="dialog" aria-modal="true" aria-label="Mobile navigation menu" data-id="element-2024">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-2025">
          <span className="text-lg font-bold text-white" data-id="element-2026">Menu</span>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#3ab795]" aria-label="Close menu" data-id="element-2027">
            <X className="w-6 h-6 text-white" data-id="element-2028" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col p-6 gap-2" aria-label="Mobile navigation" data-id="element-2029">
          {links.map(link => <a key={link.href} href={link.href} onClick={onClose} className="px-4 py-3 text-base font-semibold text-[#b8bcc8] hover:text-[#3ab795] hover:bg-white/5 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#3ab795]" data-id="element-2030">
              {link.label}
            </a>)}
        </nav>

        {/* CTA Buttons */}
        <div className="p-6 border-t border-white/10 flex flex-col gap-3" data-id="element-2031">
          <a href="./login" className="w-full px-6 py-3 text-center font-bold text-[#b8bcc8] border-2 border-white/10 rounded-xl hover:border-[#3ab795] hover:bg-white/5 transition-all focus:outline-none focus:ring-2 focus:ring-[#3ab795]" data-id="element-2032">
            Log In
          </a>
          <a href="./trial" className="w-full px-6 py-3 text-center font-bold text-white bg-[#3ab795] rounded-xl hover:bg-[#2d9f7e] transition-all focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#0a0a0a]" data-id="element-2033">
            Join Beta
          </a>
        </div>
      </div>
    </>;
}
/**
 * Mobile menu toggle button
 */
interface MobileMenuButtonProps {
  onClick: () => void;
  isOpen: boolean;
}
export function MobileMenuButton({
  onClick,
  isOpen
}: MobileMenuButtonProps) {
  return <button onClick={onClick} className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#3ab795]" aria-label={isOpen ? 'Close menu' : 'Open menu'} aria-expanded={isOpen} aria-controls="mobile-menu" data-id="element-2034">
      <Menu className="w-6 h-6 text-white" data-id="element-2035" />
    </button>;
}